'use strict';
const MomentTZ = require('moment-timezone');
const sortBy = require('lodash/sortBy');
const get = require('lodash/get');
const isArray = require('lodash/isArray');

const Globals = require('../../../app/globals');
const StringUtil = require('../../../app/shared/StringUtil');
const TraitsUtil = require('../../../app/shared/TraitsUtil');
const Strings = require('../../../app/shared/Strings');

const ExportsUtil = {
    _escapeCommas: function(string) {
        if (typeof string !== 'string') {
            return string;
        }
        
        string = string || '';
        // escape commas in a cell by surrounding the cell with double quotes
        // double quotes are escaped by doubling the double quotes i.e. " -> ""
        return '"' + string.replace(/\"/g, '""') + '"';
    },

    _generateCsvText(rows) {
        let csvContent = '';
        rows.forEach((rowArray) => {
           let row = rowArray.join(",");
           csvContent += row + "\r\n";
        });

        return csvContent;
    },

    // generate CSV string of assignment scores table
    generateAssignmentScoreCsvData: function(AssignmentStore, assignmentHash, sortStudentsFunc) {
        let tableArray = [];
        const assignmentData = AssignmentStore.getStateAssignment(assignmentHash).assignmentData;
        const activity = assignmentData.activity
        const showFractionScore = !!activity && TraitsUtil.shouldShowFractionScore(activity.traits);
        const hideOverallScore = !!activity && TraitsUtil.shouldHideOverallScore(activity.traits);
        const isMultiplayer = !!activity && TraitsUtil.isMultiplayer(activity.traits);
        const isTeam = !!activity && TraitsUtil.isTeam(activity.traits);
        const sortedScoreCategories = AssignmentStore.getScoresCategoriesForAssignment(assignmentHash, true);
        const numScoreCategories = sortedScoreCategories.length;
        const usersToTeams = {};
        const usersToGames = {};

        if (isMultiplayer) {
            Object.keys(assignmentData.usersToAllActiveAttempts).forEach((attemptId) => {
                const attemptDisplayName = get(assignmentData.allActiveAttempts[attemptId], 'name', Strings['en'].common.UNKNOWN);
                assignmentData.usersToAllActiveAttempts[attemptId].forEach((u2att) => {
                    usersToGames[u2att.user_id] = attemptDisplayName;
                });
            });
        }

        if (isTeam) {
            assignmentData.teams.forEach((team) => {
                const teamDisplayName = team.display_name;
                team.users.forEach((userId) => {
                    usersToTeams[userId] = teamDisplayName;
                });
            });
        }

        let headerRow = ['First Name', 'Last Name', 'Attempt'];
        if (isMultiplayer) {
            headerRow.push('Game');
        }

        if (isTeam) {
            headerRow.push('Team');
        }

        if (!hideOverallScore) {
            headerRow.push('Score');
        }

        headerRow.push('Time Played');

        sortedScoreCategories.forEach((sc) => {
            if (!showFractionScore) {
                headerRow.push(ExportsUtil._escapeCommas(sc.display_name));
                headerRow.push(ExportsUtil._escapeCommas(`${sc.display_name} (Raw)`));
            }
            else {
                headerRow.push(ExportsUtil._escapeCommas(sc.display_name));
                headerRow.push(ExportsUtil._escapeCommas(`${sc.display_name} (Percent)`));
            }
        });

        let students = assignmentData.students;
        if (assignmentData.user.role_token === Globals.users.roles.STUDENT) {
            let student = students.find(student => student.id === assignmentData.user.id);
            students = [student];
        }

        const studentRows = [];
        const sortedStudents = sortStudentsFunc(students);
        sortedStudents.forEach((student) => {
            const studentAttempts = sortBy(assignmentData.allAttempts[student.id], 'attempt');
            studentAttempts.forEach((attempt) => {
                const attemptInfo = assignmentData.allAttempts[student.id].find(att => parseInt(att.id, 10) === parseInt(attempt.id, 10));
                if (!attemptInfo) {
                    console.warn(`Attempt ${attempt.id} not found.`);
                    return;
                }

                const attemptScoreInfo = assignmentData.scores_and_times.find((attScore) => {
                    return parseInt(attScore.attempt_id, 10) === parseInt(attempt.id, 10) && parseInt(attScore.user_id, 10) === parseInt(student.id, 10);
                });
                const scoreTotals = AssignmentStore.getScoreCategoryTotalsForActInstUser(assignmentHash, attemptInfo.nova_act_inst_id, student.hash);
                let studentAttemptRow = [];
                studentAttemptRow.push(ExportsUtil._escapeCommas(student.first_name));
                studentAttemptRow.push(ExportsUtil._escapeCommas(student.last_name));
                studentAttemptRow.push(attemptInfo.attempt);

                if (isMultiplayer) {
                    studentAttemptRow.push(ExportsUtil._escapeCommas(usersToGames[student.id] || 'Unassigned'));
                }

                if (isTeam) {
                    studentAttemptRow.push(ExportsUtil._escapeCommas(usersToTeams[student.id] || 'Unassigned'));
                }

                function addScoreCategoryCols(_studentAttemptRow) {
                    sortedScoreCategories.forEach((sc) => {
                        let primaryScore;
                        let secondaryScore;
                        if (scoreTotals.flags.missing_data) {
                            primaryScore = '';
                            secondaryScore = '';
                        }
                        else if (scoreTotals.flags.opt_out) {
                            primaryScore = Strings['en'].common.NOT_APPLICABLE;
                            secondaryScore = '';
                        }
                        else {
                            const scTot = scoreTotals[sc.name];
                            const percentScore = StringUtil.formatPercentScore(scTot);
                            let fractionOrRawScore;
                            if (!!scTot.raw_score) {
                                fractionOrRawScore = StringUtil.formatRawScore(scTot.raw_score, scTot.format);
                            }
                            else {
                                fractionOrRawScore = StringUtil.formatFractionScore(scTot);
                            }

                            if (!showFractionScore) {
                                primaryScore = percentScore;
                                secondaryScore = fractionOrRawScore;

                            }
                            else {
                                primaryScore = fractionOrRawScore;
                                secondaryScore = percentScore;
                            }
                        }

                        _studentAttemptRow.push(primaryScore);
                        _studentAttemptRow.push(secondaryScore);
                    });
                }

                if (attemptInfo.user_status === Globals.attempt.user_statuses.COMPLETE) {
                    if (!hideOverallScore) {
                        studentAttemptRow.push(StringUtil.formatOverallScore(attemptScoreInfo.score));
                    }

                    studentAttemptRow.push(StringUtil.getTimeStringForMs(attemptScoreInfo.time_played));
                    addScoreCategoryCols(studentAttemptRow);
                }
                else {
                    const hasValidStartedState = [
                        Globals.attempt.user_statuses.COMPLETE,
                        Globals.attempt.user_statuses.STARTED,
                        Globals.attempt.user_statuses.IN_PROGRESS,
                        Globals.attempt.user_statuses.ERROR_SENDSCORE
                    ].indexOf(attemptInfo.user_status) >= 0;

                    // add in-progress score, time played if available
                    let extraCols = !hideOverallScore ? 2 : 1;
                    if (!hideOverallScore && typeof attemptInfo.user_attempt_status_score === 'number') {
                        studentAttemptRow.push(StringUtil.formatOverallScore(attemptInfo.user_attempt_status_score));
                        extraCols -= 1;
                    }

                    if (typeof attemptInfo.user_attempt_status_time_played === 'number') {
                        studentAttemptRow.push(StringUtil.getTimeStringForMs(attemptInfo.user_attempt_status_time_played));
                        extraCols -= 1;
                    }

                    if (hasValidStartedState) {
                        addScoreCategoryCols(studentAttemptRow);
                    }
                    else {
                        for (let i = 0; i < numScoreCategories * 2 + extraCols; ++i) {
                            studentAttemptRow.push('');
                        }
                    }
                }

                studentRows.push(studentAttemptRow);
            });
        });

        const activityNameRow = [
            Strings['en'].activity.ACTIVITY_NAME,
            !!activity ? ExportsUtil._escapeCommas(activity.meta_data.display_name || activity.meta_data.title) : ''
        ];
        const dateGeneratedRow = [
            Strings['en'].export.REPORT_GEN_DATE,
            MomentTZ.tz(MomentTZ.tz.guess()).format('YYYY-MM-DD HH:mm:ss zz'),
        ];
        
        tableArray.push(headerRow);
        tableArray = tableArray.concat(studentRows);
        tableArray.push([]); // empty row
        tableArray.push(activityNameRow);
        tableArray.push(dateGeneratedRow);
        return ExportsUtil._generateCsvText(tableArray);
    },

    downloadNonCnScoreCSV: function(e, assignmentHash) {
        if (!!e && !!e.stopPropagation) e.stopPropagation();
        const self = this;
        function _getCSV() {
            var element = document.createElement('a');
            element.setAttribute('href', `/authoring/assignment/${assignmentHash}/score_csv`);
            element.style.display = 'none';
            document.body.appendChild(element);
            element.click();
            document.body.removeChild(element);
        }

        _getCSV();
    },

    downloadNonCnActivityAnalyticsCSV: function(e, assignmentHash) {
        if (!!e && !!e.stopPropagation) e.stopPropagation();
        const timezone = MomentTZ.tz.guess();

        function _getCSV(tz) {
            let query = '';
            if (!!tz) {
                query = `?tz=${encodeURIComponent(tz)}`;
            }

            var element = document.createElement('a');
            element.setAttribute('href', `/authoring/assignment/${assignmentHash}/analytics_csv${query}`);
            element.style.display = 'none';
            document.body.appendChild(element);
            element.click();
            document.body.removeChild(element);
        }

        _getCSV(timezone);
    },

    exportArrAsCsv(tableArr=[], fileName="report.csv", temptButtonText="Download") {
        let hasError = false;
        const commaEscapedArray = tableArr.map((row) => {
            if (hasError) return;

            if (!isArray(row)) {
                console.error('tableArr is expected to be array of arrays');
                hasError = true;
                return;
            }

            return row.map(cell => ExportsUtil._escapeCommas(cell));
        });

        if (hasError) return;

        const csvContent = ExportsUtil._generateCsvText(commaEscapedArray);
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        if (navigator.msSaveBlob) { // IE 10+
            navigator.msSaveBlob(blob, fileName);
        }
        else {
            let link = document.createElement("a");
            const csvUrl = URL.createObjectURL(blob);
            link.setAttribute('href', csvUrl);
            link.setAttribute("download", fileName);
            link.innerHTML= 'Download CSV file';
            document.body.appendChild(link); // Required for FF
            link.click();
            document.body.removeChild(link);
        }
    }
};

module.exports = ExportsUtil;